<template>
    <div class="userStockList">
        <UserStockListOverview></UserStockListOverview>
        <div v-if="!listLoaded" class="loading">
            <v-data-table
            item-key="name"
            class="elevation-1"
            loading
            loading-text="Loading... Please wait"
            ></v-data-table>
        </div>
        <div v-else class="uslTable">
            <v-tooltip left>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn
                        v-if="canAddEntry"
                        color="blue darken-4"
                        fab
                        dark
                        fixed
                        top
                        right
                        style="top:75px; right:40px;"
                        class="d-none d-sm-flex"
                        @click="newItemBtn_Clicked"
                        v-bind="attrs"
                        v-on="on"
                    >
                        <font-awesome-icon :icon="icons.add" class="fa-fw fa-2x"></font-awesome-icon>
                    </v-btn>
                    <v-btn
                        v-else
                        color="blue darken-4"
                        depressed
                        dark
                        fab
                        fixed
                        top
                        right
                        style="top:75px; right:40px; cursor:default;opacity:0.5"
                        class="d-none d-sm-flex"
                        v-bind="attrs"
                        v-on="on"
                    >
                        <font-awesome-icon :icon="icons.add" class="fa-fw fa-2x"></font-awesome-icon>
                    </v-btn>
                </template>
                <span v-if="canAddEntry">{{$t('UserStockWatchingList.tableHeader.addTooltip')}}</span>
                <span v-else>{{$t("UserStockWatchingList.tableHeader.entryLimitReachedTooltip")}}</span>
            </v-tooltip>
            <v-data-table
                v-if="stockList.length > 0"
                :headers="headers"
                :items="stockList"
                :item-class="itemRowBackground"
                item-key="name"
                :search="search"
                :custom-filter="filter"
                sort-by="absoluteScore"
                :sort-desc=true
                :items-per-page=-1
                locale="fr-CA"
                :header-props="{ sortIcon: null }"
                @click:row="editItem"
                class="elevation-1 stockTable">
                <template v-slot:top>
                    <v-toolbar flat height="72" color="white">
                        <div class="listName">
                            <v-select
                                v-model="listSelected"
                                :items="listNames"
                                item-text="name"
                                item-value="id"
                                rounded
                                dense
                                outlined
                                hide-details="true"
                                @change="changeList"
                            >
                                <template slot="item" slot-scope="data">
                                    <font-awesome-icon :icon="getListIconFromType(data.item.type)" class="fa-fw me-3" />
                                    <div class="cb-item">{{data.item.name}}</div>
                                </template>
                            </v-select>
                        </div>
                        <v-spacer></v-spacer>
                        <v-text-field
                            v-if="stockListLength > 0"
                            ref="searchBar"
                            v-model="search"
                            append-icon="mdi-magnify"
                            :label="$t('UserStockWatchingList.tableHeader.searchLabel')"
                            single-line
                            hide-details
                            clearable
                            dense
                            rounded
                            outlined
                            class="d-none d-sm-flex"
                        ></v-text-field>
                        <v-spacer></v-spacer>
                    </v-toolbar>
                </template>
                <template v-slot:item.symbol="{ item }">
                    <div class="entrySymbollInfo">
                        <div class="entrySymbol">{{item.symbol}}</div>
                        <CountryBadge :currency="item.currency"></CountryBadge>
                    </div>
                </template>
                <template v-slot:item.companyName="{ item }">
                    <span :class="companyNameTagClass(item.companyName)">{{displayCompanyName(item.companyName)}}</span>
                </template>
                <template v-slot:item.marketRegularPrice="{ item }">
                    <stock-price-infos :entry="item" :key="item.id + '-espi'"></stock-price-infos>
                </template>
                <template v-slot:item.change="{ item }">
                    <StockPriceChange :stockChanglistLoadedInfo="stockChangeInfo(item)"></StockPriceChange>
                </template>
                <template v-slot:item.score="{ item }">
                    <StockScoreDetailsTip :scoreDetailsInfo="item.scoreDetailsInfo">
                        <v-chip :class="getColorForScore(item.score)" dark>{{ cleanScore(item.score) }}</v-chip>
                    </StockScoreDetailsTip>
                </template>
                <template v-slot:item.marketRegularDateTime="{ item }">
                    <div class="timeSpan">
                        <stock-price-date :entry="item"></stock-price-date>
                    </div>
                </template>
                <template v-slot:item.marketCap="{ item }">
                    <span>{{AdaptIfNull(item.marketCap)}}</span>
                </template>
                <template v-slot:item.threshold200="{ item }">
                    <span>{{formatPrice(item.threshold200)}}</span>
                </template>
                <template v-slot:item.threshold400="{ item }">
                    <span>{{formatPrice(item.threshold400)}}</span>
                </template>
                <template v-slot:item.threshold600="{ item }">
                    <span>{{formatPrice(item.threshold600)}}</span>
                </template>
                <template v-slot:item.threshold800="{ item }">
                    <span>{{formatPrice(item.threshold800)}}</span>
                </template>
            </v-data-table>
            <ConfirmDialog ref="deleteItemConfirmDialog" @answered="deleteItemDialogAnswered"></ConfirmDialog>
        </div>
        <EditorPanel ref="editorPanel" @removeSymbol="deleteItem"></EditorPanel>
        <EditStockDialog ref="editStockPanel" @removeSymbol="deleteItem" @notify="showNotification"></EditStockDialog>
        <Alert ref="iAlert"></Alert>
        <SySnackbar ref="iSySnackbar"></SySnackbar>
    </div>
</template>

<script>
import userStockListMixin from './UserStockListMixin'
import UserDataHelperMixin from '../../infra/UserDataHelperMixin'
import listCardMixin from '../home/dashboard/boards/ListCardMixin'
import StockPriceInfos from '../stocks/StockPriceInfos.vue'
import StockPriceDate from '../stocks/StockPriceDate.vue'
import EditStockDialog from './edition/EditStockDialog.vue'
import SySnackbar from '../controls/SySnackbar.vue'
import UserStockListOverview from './UserStockListOverview.vue'
import DateHelper from '../../infra/DateHelper'
import StockScoreDetailsTip from '../stocks/stockScoreDetails/StockScoreDetailsTip.vue'
import CountryBadge from '../controls/CountryBadge.vue'
export default {
    components: { StockPriceInfos, StockPriceDate, EditStockDialog, SySnackbar, UserStockListOverview, StockScoreDetailsTip, CountryBadge },
    data: () => ({
        expanded: [],
        singleExpand: false,
        dialog: false,
        stocks: [],
        editedIndex: -1,
        editedItem: {
            symbol: '',
            name: '',
            threshold200: 0,
            threshold400: 0,
            threshold600: 0,
            threshold800: 0
        },
        defaultItem: {
            symbol: '',
            name: '',
            threshold200: 0,
            threshold400: 0,
            threshold600: 0,
            threshold800: 0
        }
    }),
    computed: {
        stockListLength() {
            return this.stockList ? this.stockList.length : 0;
        }
    },
    methods: {
        filter: function(value, search, item) {
            if (!search || search === "") return true;
            if (value === undefined) return false;
            /*let type = "";
            if (value === item.symbol) {
                type = "symbol";
            }
            if (value === item.symbol) {
                type = "symbol";
            }*/
            if (value === item.marketCap) {
                //type = "marketCap";
                return this.AdaptIfNull(value).toLowerCase().includes(search.toLowerCase());
            }
            else if (value === item.threshold200 || value === item.threshold400 || value === item.threshold600 || value === item.threshold800) {
                //type = "threshold"
                let valueAsString = value.toString();
                const transformedSearch = search.includes(".") ? search.replace(".", ",") : search.replace(",", ".");
                if (!isNaN(search) || !isNaN(transformedSearch)) {
                    if (!valueAsString.includes(".")) {
                        valueAsString += ".00";
                    }
                    return valueAsString.toLowerCase().includes(search.toLowerCase()) || valueAsString.toLowerCase().includes(transformedSearch.toLowerCase())
                }
            }
            if (isNaN(value)) {
                return value.toLowerCase().includes(search.toLowerCase());
            }
            else {
                /*console.log(this.formatPrice(value) + " " + search.toLowerCase());
                var searchFormated = this.formatPrice(search);
                return this.formatPrice(value).toLowerCase().includes(searchFormated);*/
            }
            //
        },
        itemRowBackground: function (item) {
            const dt = item.priceDateTime || item.marketRegularDateTime || item.lastPriceDateTime;
            if (dt) {
                const diff = DateHelper.getDiffBetweenUtcDateAndNow({ fromUTCDate: new Date(dt) });
                if (diff.days && diff.days > 10) {
                    return 'problemItemBackgroundColor';
                }
            }
            return '';
        }
    },
    mixins: [userStockListMixin, listCardMixin, UserDataHelperMixin]
}
</script>

<style scoped>
.listName{
    width: 400px;
}
th.colHeader {
    background-color: #F5F5F5;
}
.t200 span {
    border-bottom: solid 2px var(--t200);
}
.t200-sell span {
    border-bottom: solid 2px var(--t200-sell);
}
.t400 span {
    border-bottom: solid 2px var(--t400);
}
.t400-sell span {
    border-bottom: solid 2px var(--t400-sell);
}
.t600 span {
    border-bottom: solid 2px var(--t600);
}
.t800 span {
    border-bottom: solid 2px var(--t800);
}
.priceInfos{
    display: flex;
    justify-content: flex-end;
}
.entrySymbollInfo {
    display: flex;
    align-items: center;
    justify-content: space-between;
}
.entrySymbol{
    font-weight: bold;
}
span.price {
    font-weight: bold;
}
span.marketDate {
    color: #AAA;
    font-style: italic;
    font-size: small;
}
span.unknown {
    font-style: italic;
    color: #AAA;
    font-size: small;
}
.actionBtn{
    font-size: 16px;
    margin-left: 20px;
}
.stockTable .timeSpan {
    display: flex;
    justify-content: flex-end;
}
.customitem {
    display: flex;
    justify-content: space-between;
}
.listNameType{
    margin-left: 10px;
}
.stockTable >>> tbody tr :hover {
    cursor: pointer;
}

>>> .problemItemBackgroundColor{
    background-color: rgba(215,215,44,.5)
}

@media screen and (max-width: 750px) {
    .listName{
        width: 100%;
        padding-top: 5px;
    }
    .stockTable .v-toolbar__content {
        flex-direction: column;
    }
    .stockTable .v-data-table-header-mobile__wrapper .v-select {
        padding-top: 0;
    }
    .v-data-table > .v-data-table__wrapper > table > tbody > tr > td.v-data-table__mobile-row .v-chip.v-size--default {
        height: 28px;
        padding: 0 10px;
    }
    .v-data-table > .v-data-table__wrapper > table > tbody > tr > td.v-data-table__mobile-row .v-chip.transparent {
        padding: 0;
    }
    .actionBtn {
        font-size: 24px;
        margin-left: 25px;
        margin-right: 0;
    }
    .v-data-table-header-mobile{
        display: none;
    }
    .v-data-table > .v-data-table__wrapper > table > tbody > tr > td.v-data-table__mobile-row {
        height: 36px;
        min-height: 36px;
    }
    .v-data-table > .v-data-table__wrapper > table > tbody > tr > td.v-data-table__mobile-row:nth-child(even) {
        background-color: #EEE;
    }
    .v-data-table > .v-data-table__wrapper > table > tbody > tr > td.v-data-table__mobile-row:first-child {
        background-color: #555;
        color: #FFF;
    }
}
</style>
<style lang="scss" scoped>
.userStockList {
    display: flex;
    gap: 10px;
    .uslTable {
        flex: 1;
    }
}
</style>
